import { Button, type ButtonVariant } from "@/components/ui/Button";
import { useAuth } from "@/lib/auth";
import {
	useEffect,
	useRef,
	type ButtonHTMLAttributes,
	type ReactNode,
} from "react";

type Props = {
	children: ReactNode;
	onAuthenticated: () => void;
	className?: string;
	labelWhenUnauthed?: string;
	variant?: ButtonVariant;
	fullWidth?: boolean;
	iconLeft?: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "children">;

export function RequireAuthAction({
	children,
	onAuthenticated,
	className,
	labelWhenUnauthed = "Connect to continue",
	disabled,
	variant = "primary",
	fullWidth = true,
	iconLeft,
	...rest
}: Props) {
	const { isAuthenticated, isLoggingIn, login } = useAuth();
	const pendingAction = useRef(false);
	const onAuthRef = useRef(onAuthenticated);
	onAuthRef.current = onAuthenticated;

	useEffect(() => {
		if (isAuthenticated && pendingAction.current) {
			pendingAction.current = false;
			onAuthRef.current();
		}
	}, [isAuthenticated]);

	return (
		<Button
			variant={variant}
			fullWidth={fullWidth}
			loading={isLoggingIn}
			disabled={disabled}
			className={className}
			iconLeft={iconLeft}
			onClick={() => {
				if (!isAuthenticated) {
					pendingAction.current = true;
					void login();
					return;
				}
				onAuthenticated();
			}}
			{...rest}
		>
			{isAuthenticated ? children : labelWhenUnauthed}
		</Button>
	);
}
